/**
 * Salary Playground engine.
 *
 * Pure functions over (base data, reference rules, user state). The state is a thin
 * overlay of the user's changes on top of the warehouse salaries: reassignments,
 * reprices, custom signings and a trade log. Nothing here touches the DOM or
 * storage, so the page can be a straightforward renderer and the rules can be
 * unit-tested.
 *
 * Ported one-to-one from the standalone IPL salary playground; function names are
 * kept so the two stay easy to compare.
 */

export const POOL = 'POOL';

export interface SalaryTeam {
  code: string;
  name: string;
  color: string;
}

export interface SalaryReference {
  teams: Record<string, SalaryTeam>;
  codes: string[];
  /** player name -> [retaining club, capped 'C' | uncapped 'U'] */
  retained: Record<string, [string, 'C' | 'U']>;
  /** capped retentions remaining (0..5) -> minimum guideline charge in crore */
  guide: Record<number, number>;
  /** player name -> real-world trade annotation */
  traded: Record<string, { from: string; to: string; when: string; was: number | null }>;
  natUnverified: Set<string>;
  defaults: { cap: number; maxSquad: number; maxOS: number };
}

export interface BasePlayer {
  id: string;
  name: string;
  team: string;
  price: number;
  nat: string | null;
  custom: false;
}

export interface CustomPlayer {
  id: string;
  name: string;
  team: null;
  price: number;
  nat: null;
  custom: true;
}

export type Player = BasePlayer | CustomPlayer;

export interface CustomEntry {
  id: string;
  name: string;
  price: number;
}

export interface TradeLogEntry {
  label: string;
  teams: [string, string];
  prev: Array<{ id: string; assign?: string; price?: number }>;
}

export interface SalaryState {
  cap: number;
  maxSquad: number;
  maxOS: number;
  view: string;
  assign: Record<string, string>;
  price: Record<string, number>;
  os: Record<string, boolean>;
  custom: CustomEntry[];
  seq: number;
  trades: TradeLogEntry[];
}

/** Trade builder (not persisted). sel: player id -> fee the player joins at. */
export interface TradeDraft {
  a: string;
  b: string;
  sel: Record<string, number>;
}

export interface TradeItem {
  p: Player;
  id: string;
  from: string;
  to: string;
  fee: number;
}

export interface TeamStats {
  pay: number;
  diff: number;
  n: number;
  os: number;
}

export interface RetentionInfo {
  capped: Player[];
  uncapped: Player[];
  actual: number;
  guide: number;
  charge: number;
  diff: number;
}

export interface Move {
  text: string;
  delta: number;
}

export const round2 = (x: number): number => Math.round(x * 100) / 100;
export const fmt = (x: number): string => round2(x).toFixed(2);

export const freshState = (ref: SalaryReference, view?: string): SalaryState => ({
  cap: ref.defaults.cap,
  maxSquad: ref.defaults.maxSquad,
  maxOS: ref.defaults.maxOS,
  view: view || ref.codes[0],
  assign: {},
  price: {},
  os: {},
  custom: [],
  seq: 1,
  trades: [],
});

export function createSalaryEngine(base: Record<string, BasePlayer>, ref: SalaryReference) {
  const baseList = Object.values(base);

  // ---------- player accessors ----------
  const allPlayers = (S: SalaryState): Player[] => {
    const cust: CustomPlayer[] = S.custom.map((c) => ({
      id: c.id,
      name: c.name,
      team: null,
      price: c.price,
      nat: null,
      custom: true,
    }));
    return (baseList as Player[]).concat(cust);
  };
  const find = (S: SalaryState, id: string): Player | undefined => allPlayers(S).find((x) => x.id === id);
  const origTeam = (p: Player): string | null => (p.custom ? null : p.team);
  const curTeam = (S: SalaryState, p: Player): string => {
    if (S.assign[p.id] !== undefined) return S.assign[p.id];
    return p.custom ? POOL : p.team;
  };
  const curPrice = (S: SalaryState, p: Player): number =>
    S.price[p.id] !== undefined ? S.price[p.id] : p.price;
  const origPrice = (p: Player): number | null => (p.custom ? null : p.price);
  const squadOf = (S: SalaryState, t: string): Player[] => allPlayers(S).filter((p) => curTeam(S, p) === t);

  // ---------- retentions ----------
  // A player is still a retention only if he has never left the club that retained him:
  // not traded or released in real life (base club differs) and not moved in this playground.
  const retOf = (p: Player): [string, 'C' | 'U'] | null => {
    const r = ref.retained[p.name];
    return r && !p.custom && p.team === r[0] ? r : null;
  };
  const isRetained = (S: SalaryState, p: Player, useBase?: boolean): boolean => {
    const r = retOf(p);
    if (!r) return false;
    return useBase ? true : S.assign[p.id] === undefined;
  };
  const retInfo = (S: SalaryState, t: string, opts: { base?: boolean; exclude?: string } = {}): RetentionInfo => {
    const pool: Player[] = opts.base ? baseList.filter((p) => p.team === t) : squadOf(S, t);
    const retained = pool.filter(
      (p) => isRetained(S, p, opts.base) && retOf(p)![0] === t && p.id !== opts.exclude
    );
    const capped = retained.filter((p) => retOf(p)![1] === 'C');
    const price = (p: Player) => (opts.base ? p.price : curPrice(S, p));
    const actual = round2(capped.reduce((a, p) => a + price(p), 0));
    const guide = ref.guide[Math.min(capped.length, 5)];
    const charge = Math.max(guide, actual);
    return {
      capped,
      uncapped: retained.filter((p) => retOf(p)![1] === 'U'),
      actual,
      guide,
      charge,
      diff: round2(charge - actual),
    };
  };
  const capDiff = (S: SalaryState, t: string): number => retInfo(S, t).diff;
  /** Cap room actually freed by releasing/trading player p from team t. */
  const freedBy = (S: SalaryState, p: Player, t: string): number => {
    const before = retInfo(S, t).diff;
    const after = retInfo(S, t, { exclude: p.id }).diff;
    return round2(curPrice(S, p) - (after - before));
  };

  // ---------- nationality ----------
  const isOS = (S: SalaryState, p: Player): boolean =>
    p.custom ? !!S.os[p.id] : p.nat !== null && p.nat !== 'India';
  const natLabel = (S: SalaryState, p: Player): string => {
    if (p.custom) return S.os[p.id] ? 'Overseas' : 'Indian';
    if (!p.nat) return 'Nationality unknown';
    return p.nat + (ref.natUnverified.has(p.name) ? '*' : '');
  };

  // ---------- totals ----------
  const payroll = (S: SalaryState, t: string): number =>
    round2(squadOf(S, t).reduce((a, p) => a + curPrice(S, p), 0) + capDiff(S, t));
  const teamEdited = (S: SalaryState, t: string): boolean =>
    allPlayers(S).some((p) => {
      const was = origTeam(p) === t;
      const now = curTeam(S, p) === t;
      return was !== now || (now && S.price[p.id] !== undefined && S.price[p.id] !== p.price);
    });
  const teamStats = (S: SalaryState, t: string): TeamStats => {
    const sq = squadOf(S, t);
    return { pay: payroll(S, t), diff: capDiff(S, t), n: sq.length, os: sq.filter((p) => isOS(S, p)).length };
  };

  // ---------- actions (each returns a new state) ----------
  const setAssign = (S: SalaryState, id: string, team: string): SalaryState => {
    const p = find(S, id);
    if (!p) return S;
    const home = p.custom ? POOL : p.team;
    const assign = { ...S.assign };
    if (team === home) delete assign[id];
    else assign[id] = team;
    return { ...S, assign };
  };
  const moveTo = setAssign;

  const setPrice = (S: SalaryState, id: string, val: number): SalaryState => {
    const p = find(S, id);
    if (!p) return S;
    if (p.custom) {
      return { ...S, custom: S.custom.map((c) => (c.id === id ? { ...c, price: val } : c)) };
    }
    const price = { ...S.price };
    if (val === p.price) delete price[id];
    else price[id] = val;
    return { ...S, price };
  };
  /** Trade fee: like setPrice but tolerant of float noise. */
  const setPriceRaw = (S: SalaryState, id: string, fee: number): SalaryState => {
    const p = find(S, id);
    if (!p) return S;
    const b = p.custom ? S.custom.find((c) => c.id === id)!.price : p.price;
    const price = { ...S.price };
    if (Math.abs(fee - b) < 1e-9) delete price[id];
    else price[id] = fee;
    return { ...S, price };
  };

  const removeCustom = (S: SalaryState, id: string): SalaryState => {
    const assign = { ...S.assign };
    const os = { ...S.os };
    delete assign[id];
    delete os[id];
    return { ...S, custom: S.custom.filter((c) => c.id !== id), assign, os };
  };
  const addCustom = (S: SalaryState, name: string, price: number): SalaryState => {
    const id = 'c' + S.seq;
    return {
      ...S,
      seq: S.seq + 1,
      custom: [...S.custom, { id, name, price: round2(price) }],
      assign: { ...S.assign, [id]: S.view },
    };
  };
  const toggleOS = (S: SalaryState, id: string): SalaryState => {
    const os = { ...S.os };
    if (os[id]) delete os[id];
    else os[id] = true;
    return { ...S, os };
  };

  const resetTeam = (S: SalaryState, t: string): SalaryState => {
    let next: SalaryState = { ...S, assign: { ...S.assign }, price: { ...S.price }, os: { ...S.os }, custom: [...S.custom] };
    allPlayers(S).forEach((p) => {
      if (origTeam(p) === t || curTeam(S, p) === t) {
        if (p.custom) {
          if (curTeam(S, p) === t) next = removeCustom(next, p.id);
        } else {
          delete next.assign[p.id];
          delete next.price[p.id];
        }
      }
    });
    next.trades = S.trades.filter((tr) => !tr.teams.includes(t));
    return next;
  };
  const resetAll = (S: SalaryState): SalaryState => freshState(ref, S.view);

  // ---------- trades ----------
  const tradeItems = (S: SalaryState, T: TradeDraft): TradeItem[] =>
    Object.entries(T.sel)
      .map(([id, fee]) => {
        const p = find(S, id);
        if (!p) return null;
        const from = curTeam(S, p);
        return { p, id, from, to: from === T.a ? T.b : T.a, fee };
      })
      .filter((x): x is TradeItem => !!x && (x.from === T.a || x.from === T.b));
  const applyItems = (S: SalaryState, items: TradeItem[]): SalaryState =>
    items.reduce((acc, x) => setPriceRaw(setAssign(acc, x.id, x.to), x.id, x.fee), S);
  const simulateTrade = (S: SalaryState, T: TradeDraft, items: TradeItem[]) => {
    const before = { [T.a]: teamStats(S, T.a), [T.b]: teamStats(S, T.b) };
    const after_S = applyItems(S, items);
    const after = { [T.a]: teamStats(after_S, T.a), [T.b]: teamStats(after_S, T.b) };
    return { before, after };
  };
  const completeTrade = (S: SalaryState, T: TradeDraft): { S: SalaryState; label: string } | null => {
    const items = tradeItems(S, T);
    if (!items.length || T.a === T.b) return null;
    const prev = items.map((x) => ({ id: x.id, assign: S.assign[x.id], price: S.price[x.id] }));
    const label =
      `${T.a} and ${T.b}: ` + items.map((x) => `${x.p.name} to ${x.to} at ₹${fmt(x.fee)}`).join(', ');
    const next = applyItems(S, items);
    return { S: { ...next, trades: [...S.trades, { label, teams: [T.a, T.b], prev }] }, label };
  };
  const undoTrade = (S: SalaryState): SalaryState => {
    if (!S.trades.length) return S;
    const tr = S.trades[S.trades.length - 1];
    const assign = { ...S.assign };
    const price = { ...S.price };
    tr.prev.forEach((x) => {
      if (x.assign === undefined) delete assign[x.id];
      else assign[x.id] = x.assign;
      if (x.price === undefined) delete price[x.id];
      else price[x.id] = x.price;
    });
    return { ...S, assign, price, trades: S.trades.slice(0, -1) };
  };

  // ---------- moves ledger ----------
  const movesFor = (S: SalaryState, t: string) => {
    const ins: Player[] = [];
    const outs: Player[] = [];
    const rep: Player[] = [];
    allPlayers(S).forEach((p) => {
      const was = origTeam(p) === t;
      const now = curTeam(S, p) === t;
      if (now && !was) ins.push(p);
      else if (was && !now) outs.push(p);
      else if (now && was && S.price[p.id] !== undefined) rep.push(p);
    });
    const moves: Move[] = [];
    outs.forEach((p) => {
      const ct = curTeam(S, p);
      moves.push({ text: `${ct === POOL ? 'Released' : 'Traded to ' + ct}: ${p.name}`, delta: -p.price });
    });
    ins.forEach((p) => {
      const v = curPrice(S, p);
      moves.push({ text: `${p.custom ? 'Signed' : 'Acquired from ' + (origTeam(p) || 'pool')}: ${p.name}`, delta: v });
    });
    rep.forEach((p) => {
      const d = curPrice(S, p) - p.price;
      moves.push({ text: `Repriced: ${p.name} (₹${fmt(p.price)} to ₹${fmt(curPrice(S, p))})`, delta: d });
    });
    const cd = capDiff(S, t);
    const baseDiff = retInfo(S, t, { base: true }).diff;
    const dd = round2(cd - baseDiff);
    if (Math.abs(dd) > 0.001) {
      moves.push({ text: `Cap differential: ₹${fmt(baseDiff)} to ₹${fmt(cd)} (retention guideline)`, delta: dd });
    }
    const orig = round2(baseList.filter((p) => p.team === t).reduce((a, p) => a + p.price, 0) + baseDiff);
    const pay = payroll(S, t);
    return { moves, orig, pay, net: round2(pay - orig) };
  };

  return {
    allPlayers,
    find,
    origTeam,
    origPrice,
    curTeam,
    curPrice,
    squadOf,
    retOf,
    isRetained,
    retInfo,
    capDiff,
    freedBy,
    isOS,
    natLabel,
    payroll,
    teamEdited,
    teamStats,
    moveTo,
    setAssign,
    setPrice,
    setPriceRaw,
    removeCustom,
    addCustom,
    toggleOS,
    resetTeam,
    resetAll,
    tradeItems,
    applyItems,
    simulateTrade,
    completeTrade,
    undoTrade,
    movesFor,
  };
}

export type SalaryEngine = ReturnType<typeof createSalaryEngine>;
